/**
 * usePerformanceMode - Global device capability detection with user override.
 *
 * Priority order:
 *  1. localStorage override ("blk-perf-override") — user's explicit choice
 *  2. Auto-detection (device memory, cores, touch, frame rate)
 *
 * Runs detection ONCE (module-level cache), shared across all components.
 * Listens for 'perf-mode-changed' events from the Settings toggle.
 *
 * Returns: "low" | "high"
 */
import { useState, useEffect, useRef } from "react";

let _cachedMode = null;

function detectSync() {
  if (typeof navigator === "undefined") return "high";
  if (typeof window === "undefined") return "high";

  // Check user override first
  const override = localStorage.getItem("blk-perf-override");
  if (override === "low" || override === "high") return override;

  const mem = navigator.deviceMemory;
  const cores = navigator.hardwareConcurrency;
  const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  if (mem && mem <= 2) return "low";
  if (isTouch && cores && cores <= 4) return "low";
  if (isTouch && mem && mem <= 4) return "low";
  if (!isTouch && cores && cores >= 8) return "high";
  if (mem && mem >= 8) return "high";

  return null;
}

const usePerformanceMode = () => {
  const [mode, setMode] = useState(() => {
    // Always re-check override (user might have changed it)
    const override = typeof window !== "undefined" ? localStorage.getItem("blk-perf-override") : null;
    if (override === "low" || override === "high") {
      _cachedMode = override;
      return override;
    }
    if (_cachedMode) return _cachedMode;
    const sync = detectSync();
    if (sync) {
      _cachedMode = sync;
      return sync;
    }
    return null;
  });

  const rafRef = useRef(null);

  // Listen for user settings changes
  useEffect(() => {
    const handlePerfChange = () => {
      const override = localStorage.getItem("blk-perf-override");
      if (override === "low" || override === "high") {
        _cachedMode = override;
        setMode(override);
      } else {
        // Reset to auto-detect
        _cachedMode = null;
        const sync = detectSync();
        if (sync) {
          _cachedMode = sync;
          setMode(sync);
        }
      }
    };
    window.addEventListener("perf-mode-changed", handlePerfChange);
    return () => window.removeEventListener("perf-mode-changed", handlePerfChange);
  }, []);

  // Frame-rate test for inconclusive devices
  useEffect(() => {
    if (mode !== null) return;

    let frames = 0;
    let start = performance.now();
    let running = true;

    const tick = () => {
      if (!running) return;
      frames++;
      const elapsed = performance.now() - start;

      if (elapsed >= 500) {
        const fps = (frames / elapsed) * 1000;
        const result = fps < 30 ? "low" : "high";
        _cachedMode = result;
        setMode(result);
        running = false;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [mode]);

  return mode || "high";
};

export default usePerformanceMode;
